import React, { useState, useEffect } from 'react';
import { BrickCharts, ChartSource } from 'brickcharts/dist/brickcharts.es.js';

function LastFMCharts() {
  const [lastfmData, setLastfmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('top-tracks');

  const chartTypes = [
    { value: 'top-tracks', label: 'Top Tracks', icon: '🎵' },
    { value: 'top-albums', label: 'Top Albums', icon: '💿' },
    { value: 'top-artists', label: 'Top Artists', icon: '👤' },
    { value: 'top-tracks-weekly', label: 'Weekly Tracks', icon: '📅' },
    { value: 'top-tracks-monthly', label: 'Monthly Tracks', icon: '📆' }
  ];

  useEffect(() => {
    const fetchLastFM = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const brickCharts = new BrickCharts({
          enableCache: true,
          apiKeys: {
            lastfm: process.env.REACT_APP_LASTFM_API_KEY
          }
        });

        const chart = await brickCharts.getChart(chartType, ChartSource.LASTFM);
        setLastfmData(chart);
      } catch (error) {
        setError(error.message);
        console.error('Last.FM error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLastFM();
  }, [chartType]);

  const formatPlaycount = (playcount) => {
    if (!playcount) return 'N/A';
    if (playcount >= 1000000) {
      return `${(playcount / 1000000).toFixed(1)}M`;
    }
    if (playcount >= 1000) {
      return `${(playcount / 1000).toFixed(1)}K`;
    }
    return playcount.toString();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading Last.FM {chartType.replace('-', ' ').toUpperCase()}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Error Loading Last.FM Data</h3>
        <p>{error}</p>
        {error.includes('API key') && (
          <p className="api-key-note">
            Note: Last.FM API key is required. Set REACT_APP_LASTFM_API_KEY in your environment variables.
          </p>
        )}
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="lastfm-charts">
      <div className="chart-controls">
        <div className="chart-type-selector">
          <label htmlFor="lastfm-chart-type">Select Chart Type:</label>
          <select 
            id="lastfm-chart-type"
            value={chartType} 
            onChange={(e) => setChartType(e.target.value)}
          >
            {chartTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {lastfmData && (
        <div className="chart-content">
          <div className="chart-header">
            <h2>Last.FM {chartType.replace('-', ' ').toUpperCase()}</h2>
            {lastfmData.date && (
              <p className="chart-date">
                {new Date(lastfmData.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
          </div>

          <div className="chart-stats">
            <div className="stat">
              <span className="stat-label">Total Entries</span>
              <span className="stat-value">{lastfmData.totalEntries || lastfmData.entries?.length || 0}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Plays</span>
              <span className="stat-value">
                {formatPlaycount(
                  lastfmData.entries?.reduce((total, entry) => 
                    total + (entry.metadata?.playcount || 0), 0
                  )
                )}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Avg Plays</span>
              <span className="stat-value">
                {formatPlaycount(
                  Math.round(
                    lastfmData.entries?.reduce((total, entry) => 
                      total + (entry.metadata?.playcount || 0), 0
                    ) / (lastfmData.entries?.length || 1)
                  )
                )}
              </span>
            </div>
          </div>

          <div className="chart-entries">
            {lastfmData.entries?.slice(0, 50).map((entry, index) => (
              <div key={entry.rank || index} className="chart-entry">
                <span className="rank">#{entry.rank}</span>
                <div className="entry-info">
                  <span className="title">{entry.title}</span>
                  <span className="artist">{entry.artist}</span>
                </div>
                <div className="entry-details">
                  {entry.metadata?.playcount && (
                    <span className="playcount">
                      {formatPlaycount(entry.metadata.playcount)} plays
                    </span>
                  )}
                  {entry.metadata?.listeners && (
                    <span className="listeners">
                      {formatPlaycount(entry.metadata.listeners)} listeners
                    </span>
                  )}
                </div>
                {entry.metadata?.album && (
                  <span className="album">{entry.metadata.album}</span>
                )}
              </div>
            ))}
          </div>

          {lastfmData.entries?.length > 50 && (
            <div className="chart-footer">
              <p>Showing top 50 of {lastfmData.entries.length} entries</p>
            </div>
          )}
        </div>
      )}

      {!lastfmData && !loading && !error && (
        <div className="error">
          <p>No Last.FM data available</p>
        </div>
      )}
    </div>
  );
}

export default LastFMCharts; 